/**
 * Главный стор проекта — координатор и синхронизатор.
 *
 * Управляет только:
 * - Паспортом проекта (name, description)
 * - Историей изменений (undo/redo)
 * - Режимом демонстрации
 * - Синхронизацией всех слоёв в project.layers
 *
 * Все операции со слоями делегированы отдельным store:
 * - useWallsStore: стены, узлы, проёмы
 * - useFixturesStore: электрические приборы на стенах
 * - usePlumbingStore: сантехнические приборы на стенах
 * - useFloorItemsStore / usePlumbingItemsStore: предметы на полу — мебель и сантехника
 * - useFloorsStore: напольные покрытия
 * - useWiringStore: электрические кабели
 * - usePipesStore: трубы водоснабжения
 * - useCeilingStore: подвесные потолки комнат и их видимость
 * - useFurnitureFixturesStore: мебель и декор на стенах
 * - useWallFinishStore: отделка сторон стен
 * - useCommentsStore: комментарии-выноски на чертеже
 * - useUnderlayStore: подложка — фото или скан плана под чертежом
 *
 * Видимость слоёв управляется напрямую в отдельных store через
 * helper-функции setLayerVisible() и toggleLayer() из helpers.ts.
 *
 * Компоненты используют отдельные stores напрямую для операций
 * и projectStore только для паспорта и истории.
 */
import { create, type StateCreator } from "zustand";
import { isDev } from "@/shared/config";
import { getEmptyPlanLayers, uid, withDefaultLayers, type PlanLayers } from "@/shared/plan";
import type { WallMaterial } from "@/shared/catalog";
import { sampleLayers } from "../sample";
import { getAllLayers, layersChanged, restoreAllLayers } from "./helpers";
import { useEditorStore } from "../editorStore";
import {
	useWallsStore,
	useFixturesStore,
	usePlumbingStore,
	useFloorItemsStore,
	usePlumbingItemsStore,
	useFloorsStore,
	useWaterproofStore,
	useWiringStore,
	usePipesStore,
	useCeilingStore,
	useBaseboardStore,
	useRoomNamesStore,
	useFurnitureFixturesStore,
	useWallFinishStore,
	useCommentsStore,
	useUnderlayStore,
	resolveUnderlayImage,
	setUnderlayPersistenceEnabled,
} from "../layers";
import { DEFAULT_PROJECT_VIEW_3D, type Project, type ProjectView3DSettings } from "../types";
import { useScreedStore } from "../layers/screedsStore";

export const DEFAULT_PROJECT_NAME = "Новый проект";
export const DEMO_PROJECT_ID = "demo";

export interface NewWallOptions {
	thickness?: number;
	height?: number;
	material?: WallMaterial;
	bearing?: boolean;
	mounted?: boolean;
	snapTolerance?: number;
}

export interface OpeningPreset {
	/** Тип проёма — id элемента раздела «Двери» или «Окна» */
	item?: string;
	width: number;
	height: number;
	sill: number;
}

/** Новый пустой проект. */
export const createProject = (patch: Partial<Project> = {}): Project => ({
	id: uid("p"),
	name: DEFAULT_PROJECT_NAME,
	description: "",
	layers: getEmptyPlanLayers(),
	view3d: { ...DEFAULT_PROJECT_VIEW_3D },
	...patch,
});

interface DemoBackup {
	project: Project;
	past: PlanLayers[];
	future: PlanLayers[];
}

interface ProjectState {
	project: Project;
	past: PlanLayers[];
	future: PlanLayers[];
	demo: boolean;
	/** Проект и его история до входа в демо — выход из демо возвращает их как были */
	demoBackup: DemoBackup | null;
	/** Растёт, когда чертёж заменяется целиком (открыли файл, демо, очистка) — холст вписывает его заново */
	planRevision: number;

	// Паспорт и история
	setName: (name: string) => void;
	setDescription: (description: string) => void;
	setView3d: (patch: Partial<ProjectView3DSettings>) => void;
	pushHistory: () => void;
	/**
	 * Выполнить правку чертежа и записать её одним шагом истории — только если она что-то
	 * поменяла: стена, отвергнутая из-за пересечения, не оставляет в Ctrl+Z пустой шаг.
	 * Для правок, которые делаются одним действием (поставить стену, проём, кабель).
	 */
	recordEdit: <T>(edit: () => T) => T;
	undo: () => void;
	redo: () => void;
	loadProject: (project: Project) => void;
	clear: () => void;
	/**
	 * Сбросить чертёж — кнопка «Сброс» на панели инструментов. В отличие от `clear`,
	 * отменяется: текущий чертёж уходит в историю, Ctrl+Z его возвращает.
	 */
	resetPlan: () => void;
	loadSample: () => void;
	loadEmptyDemo: () => void;
	exitDemo: () => void;
}

const MAX_HISTORY = 60;

const createProjectStore: StateCreator<ProjectState> = (set, get) => {
	const loadDemo = (empty: boolean) => {
		const current = get();
		setUnderlayPersistenceEnabled(false);
		const demoBackup = current.demo
			? current.demoBackup
			: {
					project: { ...current.project, layers: getAllLayers() },
					past: current.past,
					future: current.future,
				};
		const layers = empty ? getEmptyPlanLayers() : sampleLayers();
		restoreAllLayers(layers);
		useEditorStore.getState().setSelection(null);
		set((state) => ({
			project: {
				id: DEMO_PROJECT_ID,
				name: empty ? "Пустой проект" : "Демо",
				description: empty ? "" : "Пример квартиры с автоматическим расчётом размеров и проводки",
				layers,
				view3d: state.project.view3d,
			},
			past: [],
			future: [],
			demo: true,
			demoBackup,
			planRevision: state.planRevision + 1,
		}));
	};

	return {
		project: createProject(),
		past: [],
		future: [],
		demo: false,
		demoBackup: null,
		planRevision: 0,

		setName: (name) => set((state) => ({ project: { ...state.project, name } })),
		setDescription: (description) =>
			set((state) => ({ project: { ...state.project, description } })),
		setView3d: (patch) =>
			set((state) => ({
				project: { ...state.project, view3d: { ...state.project.view3d, ...patch } },
			})),

		pushHistory: () => {
			set((state) => ({
				past: [...state.past, getAllLayers()].slice(-MAX_HISTORY),
				future: [],
			}));
		},

		recordEdit: (edit) => {
			const before = getAllLayers();
			const result = edit();
			if (layersChanged(before, getAllLayers())) {
				set((state) => ({
					past: [...state.past, before].slice(-MAX_HISTORY),
					future: [],
				}));
			}
			return result;
		},

		undo: () => {
			const state = get();
			if (state.past.length === 0) return;

			const layersToRestore = state.past[state.past.length - 1];
			// Снимок «как сейчас» — до восстановления: после него слои уже прошлые,
			// и «Повторить» вернуло бы на то же место
			const present = getAllLayers();
			restoreAllLayers(layersToRestore);

			set((current) => ({
				project: { ...current.project, layers: layersToRestore },
				past: current.past.slice(0, -1),
				future: [present, ...current.future].slice(0, MAX_HISTORY),
			}));
		},

		redo: () => {
			const state = get();
			if (state.future.length === 0) return;

			const layersToRestore = state.future[0];
			const present = getAllLayers();
			restoreAllLayers(layersToRestore);

			set((current) => ({
				project: { ...current.project, layers: layersToRestore },
				past: [...current.past, present].slice(-MAX_HISTORY),
				future: current.future.slice(1),
			}));
		},

		loadProject: (loaded) => {
			// Проект мог быть сохранён до появления нового слоя (комментарии) — в том числе
			// в локальной копии браузера, которая не проходит разбор файла: дополняем здесь,
			// иначе код, читающий новый слой, упадёт на старом проекте
			const rawLayers = withDefaultLayers(loaded.layers);
			const layers: PlanLayers = {
				...rawLayers,
				underlay: {
					...rawLayers.underlay,
					image: resolveUnderlayImage(rawLayers.underlay.image),
				},
			};
			const project = { ...loaded, layers };
			restoreAllLayers(project.layers);
			set((state) => ({
				project,
				past: [],
				future: [],
				demo: false,
				demoBackup: null,
				planRevision: state.planRevision + 1,
			}));
		},

		clear: () => {
			const empty = getEmptyPlanLayers();
			restoreAllLayers(empty);
			set((state) => ({
				project: { ...state.project, layers: empty },
				past: [],
				future: [],
				demo: false,
				demoBackup: null,
				planRevision: state.planRevision + 1,
			}));
		},

		resetPlan: () => {
			get().pushHistory();
			const empty = getEmptyPlanLayers();
			restoreAllLayers(empty);
			set((state) => ({ project: { ...state.project, layers: empty } }));
		},

		loadSample: () => loadDemo(false),
		loadEmptyDemo: () => loadDemo(true),

		exitDemo: () => {
			const { demo, demoBackup } = get();
			if (!demo) return;
			setUnderlayPersistenceEnabled(true);
			if (demoBackup === null) {
				get().clear();
				return;
			}
			// Возвращаем чертёж, название и историю правок такими, какими они были до демо
			restoreAllLayers(demoBackup.project.layers);
			// Выделенное в демо в своём проекте не существует
			useEditorStore.getState().setSelection(null);
			set((state) => ({
				project: demoBackup.project,
				past: demoBackup.past,
				future: demoBackup.future,
				demo: false,
				demoBackup: null,
				planRevision: state.planRevision + 1,
			}));
		},
	};
};

export const useProjectStore = create<ProjectState>(createProjectStore);

// Подписываемся на изменения во всех layer stores для автоматической синхронизации
useWallsStore.subscribe(() => {
	useProjectStore.setState((s) => ({
		project: { ...s.project, layers: getAllLayers() },
	}));
});
useFixturesStore.subscribe(() => {
	useProjectStore.setState((s) => ({
		project: { ...s.project, layers: getAllLayers() },
	}));
});
usePlumbingStore.subscribe(() => {
	useProjectStore.setState((s) => ({
		project: { ...s.project, layers: getAllLayers() },
	}));
});
usePlumbingItemsStore.subscribe(() => {
	useProjectStore.setState((s) => ({
		project: { ...s.project, layers: getAllLayers() },
	}));
});
useFloorItemsStore.subscribe(() => {
	useProjectStore.setState((s) => ({
		project: { ...s.project, layers: getAllLayers() },
	}));
});
useFloorsStore.subscribe(() => {
	useProjectStore.setState((s) => ({
		project: { ...s.project, layers: getAllLayers() },
	}));
});
useWaterproofStore.subscribe(() => {
	useProjectStore.setState((s) => ({
		project: { ...s.project, layers: getAllLayers() },
	}));
});
useScreedStore.subscribe(() => {
	useProjectStore.setState((s) => ({
		project: { ...s.project, layers: getAllLayers() },
	}));
});
useWiringStore.subscribe(() => {
	useProjectStore.setState((s) => ({
		project: { ...s.project, layers: getAllLayers() },
	}));
});
usePipesStore.subscribe(() => {
	useProjectStore.setState((s) => ({
		project: { ...s.project, layers: getAllLayers() },
	}));
});
useCeilingStore.subscribe(() => {
	useProjectStore.setState((s) => ({
		project: { ...s.project, layers: getAllLayers() },
	}));
});
useBaseboardStore.subscribe(() => {
	useProjectStore.setState((s) => ({
		project: { ...s.project, layers: getAllLayers() },
	}));
});
useRoomNamesStore.subscribe(() => {
	useProjectStore.setState((s) => ({
		project: { ...s.project, layers: getAllLayers() },
	}));
});
useFurnitureFixturesStore.subscribe(() => {
	useProjectStore.setState((s) => ({
		project: { ...s.project, layers: getAllLayers() },
	}));
});
useWallFinishStore.subscribe(() => {
	useProjectStore.setState((s) => ({
		project: { ...s.project, layers: getAllLayers() },
	}));
});
useCommentsStore.subscribe(() => {
	useProjectStore.setState((s) => ({
		project: { ...s.project, layers: getAllLayers() },
	}));
});
useUnderlayStore.subscribe(() => {
	useProjectStore.setState((s) => ({
		project: { ...s.project, layers: getAllLayers() },
	}));
});

// В dev-режиме удобно смотреть состояние проекта из консоли браузера.
if (isDev && typeof window !== "undefined") {
	(window as unknown as Record<string, unknown>).projectStore = useProjectStore;
}
